import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getClassroom, getExercises, createTemplateFromExercise, getExerciseReport, allowRetype, allowQuizRetake, getClassroomLiveStatus, deleteExercise } from '../lib/api';
import { toast } from 'sonner';
import { 
  Keyboard, Plus, Users, ArrowLeft, Copy, Trophy, RotateCcw, Trash2,
  ChevronRight, BarChart3, BookOpen, FolderPlus, Eye, X, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';

const ClassroomView = () => {
  const navigate = useNavigate();
  const { classroomId } = useParams();
  const [classroom, setClassroom] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingTemplate, setSavingTemplate] = useState(null);
  
  // Exercise detail modal state
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [exerciseReport, setExerciseReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  
  // Live status polling state
  const [liveStatuses, setLiveStatuses] = useState({});

  const handleViewExercise = async (exerciseId) => {
    setLoadingReport(true);
    try {
      const res = await getExerciseReport(exerciseId);
      setExerciseReport(res.data);
      setSelectedExercise(exerciseId);
    } catch (error) {
      toast.error('Failed to load exercise details');
    } finally {
      setLoadingReport(false);
    }
  };

  const closeExerciseModal = () => {
    setSelectedExercise(null);
    setExerciseReport(null);
  };

  const handleSaveAsTemplate = async (e, exerciseId) => {
    e.stopPropagation();
    setSavingTemplate(exerciseId);
    try {
      await createTemplateFromExercise(exerciseId);
      toast.success('Saved to library!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTemplate(null);
    }
  };

  const handleAllowRetype = async (exerciseId, studentId, studentName) => {
    if (!window.confirm(`Allow ${studentName} to retype this exercise? Their previous attempt will be archived.`)) {
      return;
    }
    
    try {
      await allowRetype(exerciseId, studentId, 'Teacher requested retype for verification');
      toast.success(`${studentName} can now retype the exercise`);
      // Refresh the report
      const res = await getExerciseReport(exerciseId);
      setExerciseReport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allow retype');
    }
  };

  const handleAllowQuizRetake = async (exerciseId, studentId, studentName, previousScore) => {
    if (!window.confirm(`Allow ${studentName} to retake the quiz?\n\nTheir previous quiz score (${previousScore}%) will be archived. Their typing results will remain unchanged.`)) {
      return;
    }
    
    try {
      await allowQuizRetake(exerciseId, studentId, 'Teacher allowed quiz retake');
      toast.success(`${studentName} can now retake the quiz`);
      // Refresh the report
      const res = await getExerciseReport(exerciseId);
      setExerciseReport(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to allow quiz retake');
    }
  };

  const handleDeleteExercise = async (e, exerciseId, exerciseTitle) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${exerciseTitle}"?\n\nThis will permanently remove the assignment and all student submissions. This action cannot be undone.`)) {
      return;
    }
    
    try {
      await deleteExercise(exerciseId);
      toast.success(`"${exerciseTitle}" deleted successfully`);
      // Remove from local state
      setExercises(prev => prev.filter(ex => ex.exercise_id !== exerciseId));
      // Close modal if this exercise was selected
      if (selectedExercise === exerciseId) {
        closeExerciseModal();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete exercise');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classroomRes, exercisesRes] = await Promise.all([
          getClassroom(classroomId),
          getExercises(classroomId)
        ]);
        setClassroom(classroomRes.data);
        setExercises(exercisesRes.data);
      } catch (error) {
        toast.error('Failed to load classroom');
        navigate('/teacher');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [classroomId, navigate]);

  // Live status polling - every 10 seconds
  useEffect(() => {
    const fetchLiveStatus = async () => {
      try {
        const res = await getClassroomLiveStatus(classroomId);
        setLiveStatuses(res.data.student_statuses || {});
      } catch (error) {
        // Silently fail - live status is non-critical
        console.warn('Failed to fetch live status:', error);
      }
    };

    // Initial fetch
    fetchLiveStatus();

    // Poll every 10 seconds
    const interval = setInterval(fetchLiveStatus, 10000);

    return () => clearInterval(interval);
  }, [classroomId]);

  const copyClassCode = async () => {
    if (!classroom?.class_code) return;
    const code = classroom.class_code;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success('Class code copied!');
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success('Class code copied!');
      } catch {
        toast.error('Could not copy. Code: ' + code);
      }
      document.body.removeChild(textArea);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-white/5 bg-background-paper/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/teacher')}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-bold">{classroom?.name}</h1>
              <p className="text-text-secondary text-sm">Grade {classroom?.grade_level}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div 
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
              onClick={copyClassCode}
              data-testid="class-code-display"
            >
              <span className="text-text-secondary text-sm">Class Code:</span>
              <span className="font-mono text-primary font-bold">{classroom?.class_code}</span>
              <Copy className="w-4 h-4 text-primary" />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <Card 
            className="md:col-span-8 card-glass p-6 cursor-pointer group"
            onClick={() => navigate(`/create-exercise?classroom=${classroomId}`)}
            data-testid="create-exercise-card"
          >
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center group-hover:neon-border transition-all">
                <Plus className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h3 className="font-heading text-xl font-bold mb-1 group-hover:text-primary transition-colors">
                  Create New Exercise
                </h3>
                <p className="text-text-secondary">
                  Generate AI-powered typing passages and quizzes aligned with ELA standards
                </p>
              </div>
            </div>
          </Card>
          
          <Card 
            className="md:col-span-3 card-glass p-6 cursor-pointer group"
            onClick={() => navigate(`/class-reports/${classroomId}`)}
            data-testid="view-reports-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-secondary/20 flex items-center justify-center group-hover:neon-border-secondary transition-all">
                <BarChart3 className="w-6 h-6 text-secondary" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold group-hover:text-secondary transition-colors">
                  View Reports
                </h3>
                <p className="text-text-muted text-sm">Objectives & Assignment Scores</p>
              </div>
            </div>
          </Card>

          <Card 
            className="md:col-span-3 card-glass p-6 cursor-pointer group"
            onClick={() => navigate(`/leaderboard/${classroomId}`)}
            data-testid="view-leaderboard-card"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-neon-green/20 flex items-center justify-center group-hover:border group-hover:border-neon-green transition-all">
                <Trophy className="w-6 h-6 text-neon-green" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold group-hover:text-neon-green transition-colors">
                  Leaderboard
                </h3>
                <p className="text-text-muted text-sm">Speed, Accuracy & Quiz Rankings</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Students Section */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Students ({classroom?.students?.length || 0})
          </h2>
          {classroom?.students?.length === 0 ? (
            <Card className="card-glass p-8 text-center" data-testid="no-students">
              <Users className="w-10 h-10 text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No students have joined yet.</p>
              <p className="text-text-muted text-sm mt-1">
                Share the class code <span className="text-primary font-mono">{classroom?.class_code}</span> with your students
              </p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {classroom?.students?.map((student) => {
                // Get live status for this student
                const studentLiveStatus = liveStatuses[student.user_id] || [];
                const activeSession = studentLiveStatus.find(s => 
                  s.status === 'typing' || s.status === 'taking_quiz' || s.status === 'in_progress'
                );
                const completedCount = studentLiveStatus.filter(s => s.status === 'completed').length;
                
                // Determine status badge
                let statusBadge = null;
                if (activeSession) {
                  if (activeSession.status === 'typing') {
                    statusBadge = { text: 'Typing...', color: 'bg-primary/20 text-primary', pulse: true };
                  } else if (activeSession.status === 'taking_quiz') {
                    statusBadge = { text: 'Taking Quiz', color: 'bg-secondary/20 text-secondary', pulse: true };
                  } else if (activeSession.status === 'in_progress') {
                    statusBadge = { text: 'In Progress', color: 'bg-neon-yellow/20 text-neon-yellow', pulse: true };
                  }
                } else if (completedCount > 0) {
                  statusBadge = { text: `${completedCount} Done`, color: 'bg-neon-green/20 text-neon-green', pulse: false };
                }
                
                return (
                <Card 
                  key={student.user_id}
                  className="card-glass p-4"
                  data-testid={`student-card-${student.user_id}`}
                >
                  <div className="flex items-center gap-3">
                    {student.picture ? (
                      <img 
                        src={student.picture} 
                        alt={student.name} 
                        className="w-10 h-10 rounded-full"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="font-bold text-primary">
                          {student.name?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{student.name}</p>
                      <p className="text-text-muted text-xs truncate">{student.email}</p>
                    </div>
                    {statusBadge && (
                      <div className={`px-2 py-1 rounded text-xs whitespace-nowrap ${statusBadge.color} ${statusBadge.pulse ? 'animate-pulse' : ''}`}>
                        {statusBadge.text}
                      </div>
                    )}
                  </div>
                </Card>
              )})}
            </div>
          )}
        </div>

        {/* Exercises Section */}
        <div>
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-secondary" />
            Exercises ({exercises.length})
          </h2>
          {exercises.length === 0 ? (
            <Card className="card-glass p-8 text-center" data-testid="no-exercises">
              <Keyboard className="w-10 h-10 text-text-muted mx-auto mb-3" />
              <p className="text-text-secondary">No exercises created yet.</p>
              <Button 
                className="btn-primary rounded-sm mt-4"
                onClick={() => navigate(`/create-exercise?classroom=${classroomId}`)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Exercise
              </Button>
            </Card>
          ) : (
            <div className="space-y-4">
              {exercises.map((exercise) => (
                <Card 
                  key={exercise.exercise_id}
                  className="card-glass p-6 group cursor-pointer hover:border-primary/30 transition-all"
                  onClick={() => handleViewExercise(exercise.exercise_id)}
                  data-testid={`exercise-card-${exercise.exercise_id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center group-hover:neon-border transition-all">
                        <Keyboard className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-bold group-hover:text-primary transition-colors">
                          {exercise.title}
                        </h3>
                        <p className="text-text-secondary text-sm">
                          {exercise.objectives?.length > 1 
                            ? exercise.objectives.map(o => o.code).join(', ')
                            : exercise.objective_code} • {exercise.duration_minutes} min • {exercise.target_wpm} WPM
                        </p>
                        <p className="text-text-muted text-xs mt-1">
                          Assigned: {exercise.created_at ? new Date(exercise.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleSaveAsTemplate(e, exercise.exercise_id)}
                        disabled={savingTemplate === exercise.exercise_id}
                        className="text-text-muted hover:text-neon-green"
                        data-testid={`save-template-${exercise.exercise_id}`}
                      >
                        <FolderPlus className="w-4 h-4 mr-1" />
                        {savingTemplate === exercise.exercise_id ? 'Saving...' : 'Save to Library'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => handleDeleteExercise(e, exercise.exercise_id, exercise.title)}
                        className="text-text-muted hover:text-neon-red hover:bg-neon-red/10"
                        data-testid={`delete-exercise-${exercise.exercise_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                      <Eye className="w-5 h-5 text-text-muted group-hover:text-primary transition-colors" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Exercise Detail Modal */}
      <Dialog open={!!selectedExercise} onOpenChange={(open) => !open && closeExerciseModal()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background-paper border-white/10">
          {loadingReport ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : exerciseReport ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">
                  {exerciseReport.exercise.title}
                </DialogTitle>
                <p className="text-text-secondary text-sm mt-1">
                  {exerciseReport.exercise.objectives?.length > 1 
                    ? exerciseReport.exercise.objectives.map(o => o.code).join(', ')
                    : exerciseReport.exercise.objective_code} • Grade {exerciseReport.exercise.grade_level} • 
                  {exerciseReport.exercise.duration_minutes} min • {exerciseReport.exercise.target_wpm} WPM
                </p>
              </DialogHeader>

              {/* Passage Section */}
              <div className="mt-6">
                <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  Reading Passage
                </h3>
                <div className="bg-black/30 rounded-lg p-4 max-h-60 overflow-y-auto border border-white/10">
                  <p className="text-text-primary leading-relaxed whitespace-pre-wrap">
                    {exerciseReport.exercise.passage}
                  </p>
                </div>
              </div>

              {/* Quiz Questions Section */}
              <div className="mt-6">
                <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                  <Keyboard className="w-5 h-5 text-secondary" />
                  Quiz Questions ({exerciseReport.exercise.quiz_questions.length})
                </h3>
                <div className="space-y-4">
                  {exerciseReport.exercise.quiz_questions.map((q, idx) => (
                    <div key={idx} className="bg-black/30 rounded-lg p-4 border border-white/10">
                      <p className="font-medium mb-3">
                        <span className="text-primary mr-2">{idx + 1}.</span>
                        {q.question}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 ml-4">
                        {q.options?.map((opt, optIdx) => (
                          <div 
                            key={optIdx}
                            className={`text-sm px-3 py-2 rounded ${
                              opt.startsWith(q.correct_answer) 
                                ? 'bg-neon-green/20 text-neon-green border border-neon-green/30' 
                                : 'text-text-secondary'
                            }`}
                          >
                            {opt}
                          </div>
                        ))}
                      </div>
                      {q.explanation && (
                        <p className="text-text-muted text-sm mt-3 ml-4 italic">
                          💡 {q.explanation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Results Section */}
              {exerciseReport.student_results.length > 0 && (
                <div className="mt-6">
                  <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-neon-yellow" />
                    Student Results ({exerciseReport.summary.completed_count}/{exerciseReport.summary.total_students} completed)
                  </h3>
                  <div className="space-y-3">
                    {exerciseReport.student_results.map((result) => {
                      // Calculate final score if not present (backwards compatibility)
                      const accuracy = result.accuracy || 0;
                      const completion = result.completion || 0;
                      const quizScore = result.quiz_score || 0;
                      const typedWords = result.typed_words || 0;
                      const isEmptySubmission = result.empty_submission || (typedWords === 0 && accuracy === 0 && completion === 0);
                      const finalScore = result.final_score || 
                        ((accuracy > 0 || completion > 0 || quizScore > 0) 
                          ? Math.round((accuracy + completion + quizScore) / 3 * 10) / 10 
                          : 0);
                      
                      // Check for paste/cheating pattern: high completion + very low accuracy
                      const pasteSuspected = result.paste_suspected || (completion >= 80 && accuracy < 20);
                      const wasTerminated = result.termination_reason === 'cheat_detected';
                      
                      return (
                      <div key={result.session_id} className={`bg-black/30 rounded-lg p-4 border ${wasTerminated ? 'border-neon-red/70' : isEmptySubmission ? 'border-neon-red/50' : pasteSuspected ? 'border-neon-orange/50' : 'border-white/10'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div>
                              <p className="font-medium">{result.student_name}</p>
                              <p className="text-text-muted text-xs">{result.student_email}</p>
                            </div>
                            {wasTerminated && (
                              <span className="px-2 py-0.5 bg-neon-red/30 text-neon-red text-xs rounded-full font-bold">
                                🚫 Terminated
                              </span>
                            )}
                            {isEmptySubmission && !wasTerminated && (
                              <span className="px-2 py-0.5 bg-neon-red/20 text-neon-red text-xs rounded-full">
                                No Work
                              </span>
                            )}
                            {pasteSuspected && !isEmptySubmission && !wasTerminated && (
                              <span className="px-2 py-0.5 bg-neon-orange/20 text-neon-orange text-xs rounded-full">
                                ⚠️ Paste Suspected
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <div className="text-center">
                              <p className="text-text-muted text-xs">Accuracy</p>
                              <p className={`font-mono font-bold ${
                                accuracy >= 80 ? 'text-neon-green' : 
                                accuracy >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                              }`}>{accuracy}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-text-muted text-xs">Completion</p>
                              <p className={`font-mono font-bold ${
                                completion >= 80 ? 'text-neon-green' : 
                                completion >= 50 ? 'text-neon-yellow' : 'text-neon-red'
                              }`}>{completion}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-text-muted text-xs">WPM</p>
                              <p className={`font-mono font-bold ${
                                result.wpm_flagged ? 'text-neon-red' : 'text-secondary'
                              }`}>
                                {result.actual_wpm}
                                {result.wpm_flagged && <span className="text-xs ml-1">⚠️</span>}
                              </p>
                            </div>
                            <div className="text-center">
                              <p className="text-text-muted text-xs">Quiz</p>
                              <p className={`font-mono font-bold ${
                                quizScore >= 80 ? 'text-neon-green' : 
                                quizScore >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                              }`}>{quizScore}%</p>
                            </div>
                            <div className="text-center">
                              <p className="text-text-muted text-xs">Final</p>
                              <p className={`font-mono font-bold ${
                                finalScore >= 80 ? 'text-neon-green' : 
                                finalScore >= 60 ? 'text-neon-yellow' : 'text-neon-red'
                              }`}>{finalScore}%</p>
                            </div>
                            <div className={`px-2 py-1 rounded text-xs ${
                              result.status === 'completed' 
                                ? 'bg-neon-green/20 text-neon-green' 
                                : result.status === 'typing_complete'
                                  ? 'bg-neon-yellow/20 text-neon-yellow'
                                  : result.status === 'taking_quiz'
                                    ? 'bg-secondary/20 text-secondary'
                                    : result.status === 'typing'
                                      ? 'bg-primary/20 text-primary'
                                      : 'bg-white/10 text-text-muted'
                            }`}>
                              {result.status === 'completed' ? 'Completed' : 
                               result.status === 'typing_complete' ? 'Quiz Pending' :
                               result.status === 'taking_quiz' ? 'Taking Quiz' :
                               result.status === 'typing' ? 'Typing...' : 'In Progress'}
                            </div>
                            {/* Tab Switch Indicator */}
                            {result.tab_switches > 0 && (
                              <div 
                                className={`px-2 py-1 rounded text-xs flex items-center gap-1 ${
                                  result.tab_switch_flagged 
                                    ? 'bg-neon-red/20 text-neon-red' 
                                    : 'bg-status-warning/20 text-status-warning'
                                }`}
                                title={`Left page ${result.tab_switches} time${result.tab_switches > 1 ? 's' : ''} during quiz`}
                              >
                                <Eye className="w-3 h-3" />
                                {result.tab_switches}x
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Missed Questions */}
                        {result.missed_questions.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-sm text-neon-red mb-2 flex items-center gap-1">
                              <XCircle className="w-4 h-4" />
                              Missed {result.missed_questions.length} question(s):
                            </p>
                            <div className="space-y-2 ml-4">
                              {result.missed_questions.map((mq, idx) => (
                                <div key={idx} className="text-sm bg-neon-red/10 rounded p-2 border border-neon-red/20">
                                  <p className="text-text-primary">Q{mq.question_index + 1}: {mq.question.substring(0, 80)}...</p>
                                  <p className="text-neon-red text-xs mt-1">
                                    Answered: {mq.student_answer || 'No answer'} • Correct: {mq.correct_answer}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {result.status === 'completed' && result.missed_questions.length === 0 && (
                          <div className="mt-3 pt-3 border-t border-white/10">
                            <p className="text-sm text-neon-green flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Perfect quiz score!
                            </p>
                          </div>
                        )}
                        
                        {/* Action Buttons - shows for completed, flagged, or terminated submissions */}
                        {(result.status === 'completed' || isEmptySubmission || pasteSuspected || result.wpm_flagged || result.tab_switch_flagged || wasTerminated) && (
                          <div className="mt-3 pt-3 border-t border-white/10 flex justify-end gap-2">
                            {/* Allow Quiz Retake - only for completed submissions with quiz score */}
                            {result.status === 'completed' && result.quiz_score !== undefined && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAllowQuizRetake(selectedExercise, result.student_id, result.student_name, result.quiz_score);
                                }}
                                className="text-secondary hover:text-secondary hover:bg-secondary/10"
                                data-testid={`allow-quiz-retake-${result.student_id}`}
                              >
                                <RefreshCw className="w-4 h-4 mr-1" />
                                Quiz Retake
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAllowRetype(selectedExercise, result.student_id, result.student_name);
                              }}
                              className="text-neon-yellow hover:text-neon-orange hover:bg-neon-orange/10"
                              data-testid={`allow-retype-${result.student_id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Full Retype
                            </Button>
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassroomView;
